import { useNavigate, useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { ChevronRight } from 'lucide-react';
import {
  KPICard,
  ComparisonRow,
  ChartTooltip,
  LoadingGrid,
  formatCurrency,
  formatCurrencyCompact,
  formatHours,
} from './statisticsShared';
import type { StatisticsOutletContext } from './statisticsShared';
import { useHideAmounts } from '@/hooks/useHideAmounts';
import { FirmInsightsPanel } from '@/components/dashboard';

export default function DashboardTab() {
  const { data, loading, hasFinancialData, labels } = useOutletContext<StatisticsOutletContext>();
  const navigate = useNavigate();
  const { hideAmounts, amountClassName } = useHideAmounts();

  return (
    <div className="space-y-6">
      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {hasFinancialData ? (
          <>
            <KPICard
              title="Revenue"
              value={formatCurrency(data!.kpis.total_revenue)}
              change={data!.kpis.revenue_change_pct}
              changeLabel={`vs ${labels.previous.toLowerCase()}`}
              loading={loading}
            />
            <KPICard
              title="Outstanding"
              value={formatCurrency(data!.kpis.total_outstanding)}
              subtitle={data!.kpis.total_overdue! > 0
                ? `${formatCurrency(data!.kpis.total_overdue)} overdue`
                : 'No overdue invoices'}
              loading={loading}
            />
          </>
        ) : (
          <>
            <KPICard
              title="Active Clients"
              value={String(data?.kpis?.active_clients ?? 0)}
              subtitle="with activity this period"
              loading={loading}
            />
            <KPICard
              title="Active Matters"
              value={String(data?.kpis?.active_matters ?? 0)}
              subtitle="being worked on"
              loading={loading}
            />
            <KPICard
              title="Active Team"
              value={String(data?.kpis?.active_employees ?? 0)}
              subtitle="employees with hours"
              loading={loading}
            />
          </>
        )}
        <KPICard
          title="Hours Billed"
          value={formatHours(data?.kpis?.billable_hours)}
          subtitle={`${formatHours(data?.kpis?.total_hours)} total hours`}
          loading={loading}
        />
      </div>

      {/* Firm insight KPIs: realization, collection, utilization, DSO, AR ageing */}
      {hasFinancialData && <FirmInsightsPanel />}

      {/* Monthly Revenue Chart */}
      {!loading && data?.monthly_revenue && data.monthly_revenue.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              {hasFinancialData ? 'Monthly Revenue' : 'Monthly Hours'}
            </CardTitle>
            <CardDescription>Last 12 months</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.monthly_revenue} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="month_short"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12 }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12 }}
                  tickFormatter={(v) => hasFinancialData ? (hideAmounts ? '•••' : formatCurrencyCompact(v)) : `${v}h`}
                  width={60}
                />
                <Tooltip content={<ChartTooltip format={hasFinancialData ? 'currency' : 'hours'} />} />
                <Bar
                  dataKey={hasFinancialData ? 'revenue' : 'hours'}
                  name={hasFinancialData ? 'Revenue' : 'Hours'}
                  fill="#3b82f6"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={48}
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Period Comparison */}
      {!loading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {data?.comparison && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Period Comparison</CardTitle>
                <CardDescription>{labels.current} vs {labels.previous}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-0">
                  {hasFinancialData && data.comparison.current.revenue !== undefined && (
                    <ComparisonRow
                      label="Revenue"
                      current={data.comparison.current.revenue}
                      previous={data.comparison.previous.revenue ?? 0}
                      change={data.comparison.changes.revenue_pct ?? 0}
                      format="currency"
                    />
                  )}
                  <ComparisonRow
                    label="Hours Billed"
                    current={data.comparison.current.hours}
                    previous={data.comparison.previous.hours}
                    change={data.comparison.changes.hours_pct}
                    format="hours"
                  />
                  <ComparisonRow
                    label="Time Entries"
                    current={data.comparison.current.entries}
                    previous={data.comparison.previous.entries}
                    change={data.comparison.changes.entries_pct}
                    format="number"
                  />
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Invoicing Summary */}
      {!loading && data?.invoicing && hasFinancialData && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Invoicing</CardTitle>
            <CardDescription>Invoice status for this period</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-muted/30 rounded-lg">
                <p className="text-2xl font-bold">{data.invoicing.invoice_count}</p>
                <p className="text-xs text-muted-foreground mt-1">Total Invoices</p>
                <p className={`text-sm font-medium mt-0.5 ${amountClassName}`}>{hideAmounts ? '•••' : formatCurrency(data.invoicing.total_invoiced)}</p>
              </div>
              <div className="text-center p-4 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg">
                <p className="text-2xl font-bold text-emerald-600">{data.invoicing.paid.count}</p>
                <p className="text-xs text-muted-foreground mt-1">Paid</p>
                <p className={`text-sm font-medium text-emerald-600 mt-0.5 ${amountClassName}`}>{hideAmounts ? '•••' : formatCurrency(data.invoicing.paid.amount)}</p>
              </div>
              <div className="text-center p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
                <p className="text-2xl font-bold text-amber-600">{data.invoicing.pending.count}</p>
                <p className="text-xs text-muted-foreground mt-1">Pending</p>
                <p className={`text-sm font-medium text-amber-600 mt-0.5 ${amountClassName}`}>{hideAmounts ? '•••' : formatCurrency(data.invoicing.pending.amount)}</p>
              </div>
              <div className="text-center p-4 bg-rose-50 dark:bg-rose-950/30 rounded-lg">
                <p className="text-2xl font-bold text-rose-600">{data.invoicing.overdue.count}</p>
                <p className="text-xs text-muted-foreground mt-1">Overdue</p>
                <p className={`text-sm font-medium text-rose-600 mt-0.5 ${amountClassName}`}>{hideAmounts ? '•••' : formatCurrency(data.invoicing.overdue.amount)}</p>
              </div>
            </div>
            {data.invoicing.avg_payment_days > 0 && (
              <p className="text-sm text-muted-foreground mt-4 text-center">
                Average payment time: <span className="font-medium text-foreground">{data.invoicing.avg_payment_days} days</span>
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Quick Summaries: Top 5 Team + Top 5 Clients */}
      {!loading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {data?.team_performance && data.team_performance.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Top Team Members</CardTitle>
                    <CardDescription>By {hasFinancialData ? 'revenue' : 'hours'} this period</CardDescription>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/statistics/team')}>
                    View all
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-1">
                  {data.team_performance.slice(0, 5).map((emp) => (
                    <div
                      key={emp.id}
                      className="group flex items-center justify-between cursor-pointer rounded-md px-2 py-2 -mx-2 hover:bg-muted/60 transition-colors"
                      onClick={() => navigate(`/statistics/users/${emp.id}`)}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-muted-foreground w-5">{emp.rank}.</span>
                        <span className="text-sm font-medium text-primary underline-offset-4 group-hover:underline">{emp.name}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        {hasFinancialData && (
                          <span className={`text-sm font-semibold ${amountClassName}`}>{hideAmounts ? '•••' : formatCurrency(emp.revenue)}</span>
                        )}
                        <span className="text-sm text-muted-foreground">{emp.hours.toFixed(0)}h</span>
                        <ChevronRight className="h-4 w-4 text-muted-foreground/50 group-hover:text-foreground transition-colors" />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {data?.top_clients && data.top_clients.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Top Clients</CardTitle>
                    <CardDescription>By {hasFinancialData ? 'revenue' : 'hours'} this period</CardDescription>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/statistics/clients')}>
                    View all
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {data.top_clients.slice(0, 5).map((client) => (
                    <div key={client.id} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-muted-foreground w-5">{client.rank}.</span>
                        <span className="text-sm font-medium truncate max-w-[200px]">{client.name}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        {hasFinancialData && (
                          <span className={`text-sm font-semibold ${amountClassName}`}>{hideAmounts ? '•••' : formatCurrency(client.revenue)}</span>
                        )}
                        <span className="text-sm text-muted-foreground">{client.hours.toFixed(0)}h</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {loading && (
        <>
          <LoadingGrid count={4} />
          <Card><CardContent className="pt-6"><Skeleton className="h-[300px] w-full" /></CardContent></Card>
        </>
      )}
    </div>
  );
}
